import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { completeLinkedInSignIn } from '../lib/linkedinSignIn';

/**
 * Where Supabase sends the user back after LinkedIn (/auth/linkedin/callback). Waits for the session
 * the Supabase client reads from the URL, checks it, and continues to the page the login started for.
 * Visuals are Ethos tokens only, the same as the Google callback.
 */
export default function LinkedInCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    completeLinkedInSignIn({
      href: window.location.href,
      getSession: () => supabase.auth.getSession(),
      signOut: () => supabase.auth.signOut(),
    })
      .then((next) => {
        if (!cancelled) navigate(next, { replace: true });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'LinkedIn sign-in failed.');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main className="flex min-h-svh items-center justify-center bg-how-paper-soft px-5 text-how-space-deep">
      {error ? (
        <div className="w-full max-w-sm text-center" role="alert">
          <p className="font-body text-base">{error}</p>
          <Link
            to="/login"
            className="mt-6 inline-flex h-12 items-center justify-center rounded-xl bg-how-space-deep px-6 text-sm text-how-paper-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4" role="status">
          <div className="spinner" aria-hidden="true" />
          <p className="text-sm text-how-text-secondary">Signing you in…</p>
        </div>
      )}
    </main>
  );
}
